import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "..";
import type {
  GroupManagementCustomerOrdersResponse,
  GroupManagementFsiResponse,
  GroupManagementOverviewResponse,
  GroupManagementSaqResponse,
  GroupManagementActionCardResponse,
} from "./groupManagementOverviewAPI.types";

type GroupManagementBaseParams = {
  abo: string;
  aff: string;
};

type GroupManagementParamsWithLocale = GroupManagementBaseParams & {
  locale: string;
};

type GroupManagementRenewalsParams = GroupManagementParamsWithLocale & {
  timeZone: string;
};

export const groupManagementOverviewAPI = createApi({
  reducerPath: "groupManagementOverviewAPI",
  baseQuery: dynamicBaseQuery,
  // keep data for 1 hour
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getGroupManagementOverview: builder.query<GroupManagementOverviewResponse, GroupManagementBaseParams>({
      query: (params) => {
        const { abo, aff } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
        });

        return `/acc-action-report-v2-api/api/group-management/overview?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getWhosNewCounts: builder.query<GroupManagementActionCardResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { abo, aff, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/whosnew?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getCustomerOrders: builder.query<GroupManagementCustomerOrdersResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { aff, abo, locale } = params;
        const queryParams = new URLSearchParams({
          Aff: aff,
          Abo: abo,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/orderhistory?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getWhosCloseCounts: builder.query<GroupManagementActionCardResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { abo, aff, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/whosclose?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getBronzeGroupCounts: builder.query<GroupManagementActionCardResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { abo, aff, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/bronzegroup?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getSSIUplineCounts: builder.query<GroupManagementActionCardResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { abo, aff, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/ssiupline?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getBirthdaysCounts: builder.query<GroupManagementActionCardResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { abo, aff, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/birthdays?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getRenewalsCounts: builder.query<GroupManagementActionCardResponse, GroupManagementRenewalsParams>({
      query: (params) => {
        const { abo, aff, locale, timeZone } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
          TimeZone: timeZone,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/renewals?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getSaqCounts: builder.query<GroupManagementSaqResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { abo, aff, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/saq?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),

    getFsiCounts: builder.query<GroupManagementFsiResponse, GroupManagementParamsWithLocale>({
      query: (params) => {
        const { abo, aff, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/group-management/counts/fsiactionreport?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
  }),
});

export const {
  useGetGroupManagementOverviewQuery,
  useGetWhosNewCountsQuery,
  useGetCustomerOrdersQuery,
  useGetWhosCloseCountsQuery,
  useGetBronzeGroupCountsQuery,
  useGetSSIUplineCountsQuery,
  useGetBirthdaysCountsQuery,
  useGetRenewalsCountsQuery,
  useGetSaqCountsQuery,
  useGetFsiCountsQuery,
} = groupManagementOverviewAPI;
